/** Browse Layout
 * The purpose of this component is to do the following:
 * -- Recieve content (either outline or card shape) from a Page level components
 * -- Render loading graphic
 * -- Create consistent easily maintainable layouts across Browse pages
 * -- Pass content onto ContentPanel for rendering into rows
 * -- Pass content (outline) onto SideNavigation for rendering
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { LoadingSpinner } from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { AssignmentsProvider } from "@/assignments/useAssignmentsGet";
import Footer from "@/base/Footer";
import ContentPanel from "@/browse/ContentPanel/ContentPanel";
import { LockedContentPage } from "@/browse/LockedContentPage/LockedContentPage";
import SideNav from "@/browse/SideNav/SideNav";
import WrapRow from "@/browse/WrapRow";
import { UnlockOrPreviewContentBannerForBrowse } from "@/components/UnlockOrPreviewContentBanner";
import getUserProperties from "@/users/getUserProperties";
import { BookmarkContextProvider } from "@/users/useBookmarkContent/useBookmarkContent";
import useUser from "@/users/useUser";
import {
  filterContent,
  findRowIndex,
  isBrowsePageLocked,
} from "./BrowseLayout.utils.js";
import {
  browseLayoutGeneratorCSS,
  contentPanelContainerCSS,
  footerContainerCSS,
  pageContainerBrowseCSS,
  pageContainerCSS,
  pageTitleCSS,
} from "./BrowseLayout.styles";

const propTypes = {
  /**
   * Object used to render Pages. Derived from content API v3 response.
   */
  content: PropTypes.object,
  /**
   * String - content id of bundle.
   */
  contentId: PropTypes.string,
  /**
   * Object used to provide error information about failed requests. Derived from content API v3 response.
   */
  error: PropTypes.object,
  /**
   * Bool used to indicate requests are in progress and have not resolved. Derived from content API v3 response.
   */
  loading: PropTypes.bool,
  /**
   * Bool to determine if page is single topic
   */
  isSingleTopic: PropTypes.bool,
};

const BrowseLayout = ({ content, loading, isSingleTopic }) => {
  const { data: user } = useUser();

  const { asPath, query } = useRouter();
  const activeElementRef = useRef();
  const slugFromUrl = asPath.match(/#(.+)/)?.[1];

  const [activeItem, setActiveItem] = useState({
    activeIndex: 0,
    activeSlug: slugFromUrl,
  });
  const [skipRowIntersect, setSkipRowIntersect] = useState(false);
  const filteredContent = filterContent(content);

  // Scrolls the side nav to keep active item visible
  useEffect(() => {
    if (isSingleTopic) return;
    let timer;
    const element = activeElementRef?.current;
    if (element) {
      timer = setTimeout(() => {
        // scrollTop used in desktop if there is vertical scroll
        element.parentNode.scrollTop = element.offsetTop;
        // scrollLeft used in mobile, this setting shouldnt affect desktop
        element.parentNode.scrollLeft = element.offsetLeft;
      }, 25);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [activeItem]);

  const handleSlugChange = (slug, index) => {
    setActiveItem({
      activeIndex: index,
      activeSlug: slug,
    });
    // This is needed to quickly turn off the row intersection
    // when the user clicks into the side nav items
    setSkipRowIntersect(true);
    setTimeout(() => {
      setSkipRowIntersect(false);
    }, 250);
  };

  const handleIntersection = (entry) => {
    if (skipRowIntersect) {
      return;
    }
    if (entry.isIntersecting) {
      const rowSlug = entry?.target?.id;
      const index = findRowIndex(filteredContent, rowSlug);
      if (index >= 0) {
        setActiveItem({
          activeIndex: index,
          activeSlug: filteredContent?.[index]?.slug,
        });
      }
    }
  };

  const generateRows = (content, isSingleTopic) => {
    if (isSingleTopic) {
      return (
        <BookmarkContextProvider>
          <WrapRow
            rowMembers={content?.members}
            rowDescription={content?.description ?? ""}
          />
        </BookmarkContextProvider>
      );
    }

    // A ContentPanel component returns a collection of Horizontal Rows
    return (
      <BookmarkContextProvider>
        <ContentPanel
          members={filteredContent}
          contentId={content?.content_id}
          key={content?.content_id}
          onIntersect={handleIntersection}
        />
      </BookmarkContextProvider>
    );
  };

  const memoizedRows = useMemo(
    () => generateRows(content, isSingleTopic),
    [content, skipRowIntersect],
  );

  if (loading !== undefined && loading) {
    return <LoadingSpinner />;
  }

  if (isBrowsePageLocked(query?.slug, getUserProperties(user))) {
    return <LockedContentPage slug={query?.slug} />;
  }

  const pageContainerStyle = (isSingleTopic) => [
    pageContainerCSS(isSingleTopic),
    pageContainerBrowseCSS(isSingleTopic),
  ];

  return (
    <AssignmentsProvider>
      <div css={browseLayoutGeneratorCSS(isSingleTopic)}>
        {!isSingleTopic && (
          <SideNav
            activeItemIndex={activeItem.activeIndex}
            members={filteredContent}
            onSlugChange={handleSlugChange}
            ref={activeElementRef}
          />
        )}
        <div css={pageContainerStyle(isSingleTopic)}>
          <h1 css={pageTitleCSS} data-qa-selector="browse-content-heading">
            {content?.title}
          </h1>
          <UnlockOrPreviewContentBannerForBrowse />
          <div css={contentPanelContainerCSS}>{memoizedRows}</div>
          <div css={footerContainerCSS}>
            <Footer />
          </div>
        </div>
      </div>
    </AssignmentsProvider>
  );
};

BrowseLayout.propTypes = propTypes;
export default BrowseLayout;
